import React, { useCallback, useContext } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import AspectRatio from 'components/utils/AspectRatio'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import ModalContext from 'contexts/ModalContext'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TextLink from 'components/Luxkit/TextLink'
import SolidSquareShapeIcon from 'components/Luxkit/Icons/solid/SolidSquareShapeIcon'
import CabinDetailsModal from './CabinDetailsModal'

const Tile = styled.div`
  width: 100%;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(20)};
  margin-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    &:first-child {
      margin-top: 0;
    }
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-column-gap: ${rem(24)};
  grid-template-columns: 2fr 4fr;
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  mobileAspectRatio: '4:3',
  tabletAspectRatio: '4:3',
  desktopAspectRatio: '4:3',
  largeDesktopAspectRatio: '4:3',
  mobileWidth: '600vw',
  tabletWidth: '600vw',
  desktopWidth: '160px',
  largeDesktopWidth: '160px',
}

export interface CabinsToDisplay {
  [key: string]: {
    images: Array<{ id: string, type: 'image' }>;
    colors: Array<string>;
    info: App.CruiseShipCabinCategory;
  }
}

const descriptions: Record<string, string> = {
  Inside: 'An inside cabin is normally the most affordable option, often without a window.',
  Oceanview: 'An oceanview cabin, often called an outside cabin, is a room with a port hole or window that looks out to sea, depending on deck and location.',
  Suite: 'A suite is an especially large room within a cruise ship\'s top tier of cabins, often with a wide window and/or private verandah.',
  Balcony: 'A balcony cabin is any cruise room with a private verandah, allowing you to take in the beautiful views and fresh sea air.',
}

interface Props {
  cabin: CabinsToDisplay[keyof CabinsToDisplay];
  cabinCategories?: Array<App.CruiseShipCabinCategory>;
  showPackageOptions: () => void;
}
function CabinTile({ cabin, cabinCategories, showPackageOptions }: Props) {
  const showModal = useContext(ModalContext)

  const handleSelectDate = useCallback(() => {
    showPackageOptions()
  }, [showPackageOptions])

  const openCabinDetailsModal = useCallback(() => {
    const modalCategories = cabinCategories?.filter(category => category.cabinType === cabin.info.cabinType) ?? []
    showModal(<CabinDetailsModal
      modalCategories={modalCategories}
      cabinType={cabin.info.cabinType}
    />)
  }, [cabin.info.cabinType, showModal, cabinCategories])

  const handleToggleModal = useCallback(() => {
    if (!cabinCategories) return
    openCabinDetailsModal()
  }, [cabinCategories, openCabinDetailsModal])

  return (
    <Tile>
      <Group direction="vertical" gap={16}>
        <GridContainer key={cabin.info.imageId}>
          <AspectRatio ratio="4:3">
            <ImageCarousel
              eagerLoadFirstImage
              images={cabin.images}
              imageParams={MEDIA_IMAGE_PARAMS}
            />
          </AspectRatio>

          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <Group direction="vertical" gap={8}>
                <Heading variant="heading5" as="span">{cabin.info.cabinType}</Heading>
                <Group direction="horizontal" wrap="wrap">
                  {cabin.colors?.map((color, index) => (
                    <SolidSquareShapeIcon
                      style={{ color }}
                      key={`${color}${index}`}
                    />
                  ))}
                </Group>
              </Group>

              <CSSBreakpoint min="tablet">
                <TextButton
                  size="medium"
                  kind="secondary"
                  onClick={handleSelectDate}
                >Select a date
                </TextButton>
              </CSSBreakpoint>
            </Group>

            <CSSBreakpoint min="tablet">
              <BodyText
                variant="medium"
                className="cabin-info-desktop"
              >
                {descriptions[cabin.info.cabinType]}
              </BodyText>

              <TextLink
                size="small"
                onClick={handleToggleModal}
              >{`Show all ${cabin.info.cabinType} cabins`}
              </TextLink>
            </CSSBreakpoint>
          </Group>
        </GridContainer>

        <CSSBreakpoint max="mobile">
          <Group direction="vertical" gap={16}>
            <BodyText
              variant="medium"
              className="cabin-info-desktop"
            >
              {descriptions[cabin.info.cabinType]}
            </BodyText>

            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <TextLink
                onClick={handleToggleModal}
                size="small"
              >{`Show all ${cabin.info.cabinType} cabins`}
              </TextLink>

              <TextButton
                size="medium"
                kind="secondary"
                onClick={handleSelectDate}
              >Select a date
              </TextButton>
            </Group>
          </Group>
        </CSSBreakpoint>
      </Group>
    </Tile>
  )
}

export default CabinTile
